import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Card,
  Tag,
  Space,
  message,
  Tooltip,
  Row,
  Col,
  Statistic,
  Tabs,
  Descriptions,
  Divider,
  InputNumber,
  Alert,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  SendOutlined,
  DeliveredProcedureOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import apiClient from '../config/axios';
import { useDebounce } from '../hooks/useDebounce';
import LowStockAlerts from '../components/LowStockAlerts';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

interface Medicine {
  medicine_id: number;
  medicine_name: string;
  unit_price: number;
  quantity: number;
  seller_medicine_id?: number;
  seller_name?: string;
  seller_id?: number;
  medicine_id_original?: number;
}

interface Branch {
  branch_id: number;
  branch_name: string;
  branch_code: string;
  relationship_id?: number;
}

interface MedicineItem {
  id?: number;
  request_id?: number;
  medicine_id: number;
  medicine_name: string;
  medicine_description?: string;
  category_name?: string;
  medicine_expiry?: string;
  medicine_stock_available?: number;
  medicine_unit_price?: number | string;
  requested_quantity: number;
  approved_quantity: number;
  delivered_quantity: number;
  pending_quantity: number;
  unit_price: number | string;
}

interface InventoryRequest {
  id: number;
  request_id: number;
  request_number: string;
  branch_id: number;
  branch_name: string;
  branch_code?: string;
  branch_location?: string;
  branch_contact?: string;
  medicine_id: number;
  medicine_name: string;
  medicine_description?: string;
  category_id?: number;
  category_name?: string;
  medicine_expiry?: string;
  medicine_stock_available?: number;
  medicine_unit_price?: number | string;
  requested_quantity: number;
  approved_quantity: number;
  delivered_quantity: number;
  request_type: 'branch_request' | 'seller_direct' | 'branch_to_seller' | 'seller_to_branch';
  status: 'pending' | 'approved' | 'shipped' | 'delivered' | 'cancelled';
  unit_price: number | string;
  notes: string;
  requested_by: number;
  manager_name: string;
  manager_email?: string;
  request_date: string;
  approved_date: string;
  delivered_date: string;
  bulk_request_id: string;
  pending_quantity: number;
  status_display?: string;
  days_since_request?: number;
  seller_name?: string;
  seller_id?: number;
  medicines?: MedicineItem[]; // For grouped requests
  // Total fields for grouped requests
  total_medicines?: number;
  total_requested_quantity?: number;
  total_approved_quantity?: number;
  total_delivered_quantity?: number;
  total_pending_quantity?: number;
  total_value?: number;
}

// LowStockAlert interface removed - now handled by LowStockAlerts component

interface InventoryRequestsProps {
  branchId?: number;
}

const InventoryRequests: React.FC<InventoryRequestsProps> = ({ branchId }) => {
  // Debug logging
  console.log('InventoryRequests component rendered with branchId:', branchId);

  // Helper function to get request type display
  const getRequestTypeDisplay = (requestType: string) => {
    switch (requestType) {
      case 'branch_request':
      case 'branch_to_seller':
        return 'Branch Request';
      case 'seller_direct':
      case 'seller_to_branch':
        return 'Seller Direct';
      default:
        return 'Unknown';
    }
  };

  // Helper function to get request type color
  const getRequestTypeColor = (requestType: string) => {
    switch (requestType) {
      case 'branch_request':
      case 'branch_to_seller':
        return 'blue';
      case 'seller_direct':
      case 'seller_to_branch':
        return 'green';
      default:
        return 'default';
    }
  };
  
  // Add custom styles for dropdown options
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .ant-select-item-option {
        padding: 0 !important;
        min-height: 50px !important;
        overflow: visible !important;
      }
      .ant-select-item-option-content {
        padding: 0 !important;
        min-height: 50px !important;
        overflow: visible !important;
        display: flex !important;
        align-items: center !important;
      }
      .ant-select-dropdown .ant-select-item {
        padding: 0 !important;
        min-height: 50px !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // State management
  const [requests, setRequests] = useState<InventoryRequest[]>([]);
  const [medicines, setMedicines] = useState<Medicine[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<InventoryRequest | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [medicineSearchTerm, setMedicineSearchTerm] = useState('');
  const [form] = Form.useForm();
  
  // Filters and search
  const [filters, setFilters] = useState({
    branch_id: '',
    medicine_id: '',
    status: '',
    request_type: '',
    search: '',
    dateRange: null as any
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Low stock alerts (now handled by LowStockAlerts component)

  // Debounced search
  const debouncedSearch = useDebounce(filters.search, 500);
  const debouncedMedicineSearch = useDebounce(medicineSearchTerm, 300);

  // Get user role based on actual user authentication
  const getUserRole = useCallback(() => {
    const userStr = localStorage.getItem('user');
    console.log('=== GET USER ROLE DEBUG ===');
    console.log('Raw user string from localStorage:', userStr);
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        console.log('Parsed user object:', user);
        console.log('User properties:', {
          is_main_seller: user.is_main_seller,
          branch_id: user.branch_id,
          id: user.id,
          username: user.username
        });
        
        if (user.is_main_seller === true) {
          console.log('User role determined as: seller (main seller)');
          return 'seller';
        } else if (user.branch_id) {
          console.log('User role determined as: branch (branch manager)');
          return 'branch';
        } else {
          console.log('User role determined as: admin (no branch_id, not main seller)');
          return 'admin';
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    // Fallback to context-based detection
    console.log('Using fallback role detection, branchId:', branchId);
    const fallbackRole = branchId ? 'branch' : 'seller';
    console.log('Fallback role determined as:', fallbackRole);
    console.log('=== END GET USER ROLE DEBUG ===');
    return fallbackRole;
  }, [branchId]);

  // Fetch data functions
  const fetchRequests = useCallback(async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      console.log('=== FETCH REQUESTS CALLED ===');
      console.log('Current branchId:', branchId);
      console.log('Current page:', page);
      console.log('Current filters:', filters);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== null)
        )
      });

      // Add branch filter if branchId is provided or if user has branch
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const effectiveBranchId = branchId || userData.branch;
      
      console.log('🔍 Branch filtering debug:');
      console.log('  - branchId prop:', branchId);
      console.log('  - userData.branch:', userData.branch);
      console.log('  - userData.branch_id:', userData.branch_id); // fallback
      console.log('  - effectiveBranchId:', effectiveBranchId);
      console.log('  - userData.is_main_seller:', userData.is_main_seller);
      console.log('  - userData.is_branch_manager:', userData.is_branch_manager);
      
      if (effectiveBranchId) {
        params.append('branch_id', effectiveBranchId.toString());
        console.log('✅ Added branch_id to params:', effectiveBranchId);
        console.log('✅ Final API URL will include branch_id filter');
        console.log('✅ Source of branch_id:', branchId ? 'props' : 'localStorage');
      } else {
        console.log('❌ No branchId provided and no user branch_id found');
        console.log('❌ This will result in fetching ALL requests from ALL branches');
        console.log('❌ User data:', userData);
        
        // For sellers, add seller_id filter to show requests where they are the seller
        // This includes both requests they created AND requests created by branches for them
        const userRole = getUserRole();
        console.log('User role determined:', userRole);
        if (userRole === 'seller') {
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          console.log('Current user for seller filter:', currentUser);
          if (currentUser.id) {
            // For sellers, we need to show requests where they are the seller
            // OR requests for branches they are associated with
            // Since the API doesn't support OR queries easily, we'll use seller_id filter
            // and let the backend handle the relationship logic
            params.append('seller_id', currentUser.id.toString());
            console.log('Added seller_id to params:', currentUser.id);
            
            // TODO: The backend should be updated to also return requests for branches
            // that the seller is associated with, not just requests where they are the seller
            console.log('Note: Currently only showing requests where seller is the seller');
            console.log('To show all branch requests, the backend needs to be updated');
          }
        }
      }

      if (filters.dateRange && filters.dateRange.length === 2) {
        params.append('start_date', filters.dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', filters.dateRange[1].format('YYYY-MM-DD'));
      }

      const finalUrl = `/inventory-requests?${params}`;
      console.log('🔗 Final API URL:', finalUrl);
      
      const response = await apiClient.get(finalUrl);
      
      // Debug logging
      console.log('=== FETCH REQUESTS DEBUG ===');
      console.log('Branch ID (props):', branchId);
      console.log('Effective Branch ID:', effectiveBranchId);
      console.log('Filters:', filters);
      console.log('API URL:', finalUrl);
      console.log('Fetched requests count:', response.data.data.length);
      console.log('First few requests:', response.data.data.slice(0, 3).map((r: InventoryRequest) => ({ 
        request_number: r.request_number, 
        branch_id: r.branch_id, 
        branch_name: r.branch_name 
      })));
      console.log('Fetched requests:', response.data.data);
      console.log('Request types:', response.data.data.map((r: InventoryRequest) => ({ 
        id: r.request_id, 
        type: r.request_type, 
        branch: r.branch_id,
        status: r.status,
        number: r.request_number,
        branch_name: r.branch_name
      })));
      
      // Log all requests for admin mode (no filtering)
      if (!branchId) {
        console.log('Admin mode - showing all request types:', response.data.data.map((r: InventoryRequest) => ({
          id: r.request_id,
          branch: r.branch_id,
          branch_name: r.branch_name,
          status: r.status,
          number: r.request_number,
          type: r.request_type
        })));
      }
      console.log('=== END FETCH DEBUG ===');
      
      // Group requests by request_number to combine multiple medicines
      const groupedRequests = groupRequestsByNumber(response.data.data);
      setRequests(groupedRequests);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching requests:', error);
      message.error('Failed to fetch inventory requests');
    } finally {
      setLoading(false);
    }
  }, [filters, branchId]);

  // Group requests by request_number
  const groupRequestsByNumber = (requests: InventoryRequest[]) => {
    const grouped = requests.reduce((acc, request) => {
      const key = request.request_number;
      if (!acc[key]) {
        acc[key] = {
          id: request.id, // ✅ Added missing id field
          request_id: request.request_id, // ✅ Added request_id for backward compatibility
          request_number: key,
          bulk_request_id: request.bulk_request_id,
          branch_id: request.branch_id,
          branch_name: request.branch_name,
          branch_code: request.branch_code,
          branch_location: request.branch_location,
          branch_contact: request.branch_contact,
          seller_name: request.seller_name,
          seller_id: request.seller_id,
          manager_name: request.manager_name,
          manager_email: request.manager_email,
          request_date: request.request_date,
          approved_date: request.approved_date,
          delivered_date: request.delivered_date,
          status: request.status,
          request_type: request.request_type, // Added missing request_type field
          medicines: [],
          total_medicines: 0,
          total_requested_quantity: 0,
          total_approved_quantity: 0,
          total_delivered_quantity: 0,
          total_pending_quantity: 0,
          total_value: 0
        };
      } else {
        // Update status to the most advanced one when grouping duplicate request numbers
        const statusPriority = { 'delivered': 4, 'shipped': 3, 'approved': 2, 'pending': 1, 'cancelled': 0 };
        const currentPriority = statusPriority[acc[key].status as keyof typeof statusPriority] || 0;
        const newPriority = statusPriority[request.status as keyof typeof statusPriority] || 0;
        
        if (newPriority > currentPriority) {
          acc[key].status = request.status;
          acc[key].approved_date = request.approved_date || acc[key].approved_date;
          acc[key].delivered_date = request.delivered_date || acc[key].delivered_date;
        }
      }
      
      // Add medicine details to the grouped request
      acc[key].medicines.push({
        id: request.id, // ✅ Added missing id field
        request_id: request.request_id,
        request_number: request.request_number,
        medicine_id: request.medicine_id,
        medicine_name: request.medicine_name,
        medicine_description: request.medicine_description,
        category_name: request.category_name,
        medicine_expiry: request.medicine_expiry,
        medicine_stock_available: request.medicine_stock_available,
        medicine_unit_price: request.medicine_unit_price,
        requested_quantity: request.requested_quantity,
        approved_quantity: request.approved_quantity,
        delivered_quantity: request.delivered_quantity,
        pending_quantity: request.pending_quantity,
        unit_price: request.unit_price
      });
      
      // Update totals
      acc[key].total_medicines += 1;
      acc[key].total_requested_quantity += Number(request.requested_quantity);
      acc[key].total_approved_quantity += Number(request.approved_quantity) || 0;
      acc[key].total_delivered_quantity += Number(request.delivered_quantity) || 0;
      acc[key].total_pending_quantity += Number(request.pending_quantity);
      acc[key].total_value += (Number(request.unit_price) * Number(request.requested_quantity));
      
      return acc;
    }, {} as Record<string, any>);
    
    return Object.values(grouped);
  };

  const fetchMedicines = useCallback(async (searchTerm = '') => {
    try {
      console.log('=== FETCH MEDICINES CALLED ===');
      const userRole = getUserRole();
      console.log('User role:', userRole);
      
      if (userRole === 'seller') {
        // For sellers, fetch their specific medicines with pricing
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id) {
          const params = new URLSearchParams({
            'seller_id': currentUser.id.toString(),
            'populate[medicine][fields][0]': 'medicine_name',
            'populate[medicine][fields][1]': 'id',
            'pagination[pageSize]': '1000',
            ...(searchTerm && { 'search': searchTerm })
          });
          
          const response = await apiClient.get(`/seller-medicines?${params}`);
          console.log('Raw seller medicines response:', response.data);
          
          // Transform the response to match the Medicine interface and filter out medicines with no stock
          const sellerMedicines = response.data.data
            .map((sellerMedicine: any) => ({
              medicine_id: sellerMedicine.id, // Use seller_medicine ID for seller requests
              medicine_name: sellerMedicine.attributes?.medicine?.data?.attributes?.medicine_name || 'Unknown Medicine',
              unit_price: sellerMedicine.attributes?.unit_price || 0,
              quantity: sellerMedicine.attributes?.seller_stock_quantity || 0,
              seller_medicine_id: sellerMedicine.id, // Keep reference to seller_medicine ID
              medicine_id_original: sellerMedicine.attributes?.medicine?.data?.id // Keep reference to original medicine ID
            }))
            .filter((medicine: any) => medicine.quantity > 0); // Only show medicines with stock > 0
          
          setMedicines(sellerMedicines);
          console.log('Fetched seller medicines:', sellerMedicines);
          console.log('Number of medicines set:', sellerMedicines.length);
        } else {
          console.error('No seller ID found in user data');
          setMedicines([]);
        }
      } else {
        // For branch managers, fetch medicines from sellers who have active relationships with this branch
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.branch_id) {
          try {
            // First, get all sellers who have active relationships with this branch
            const relationshipsResponse = await apiClient.get(`/branch-seller-relationships/branch/${currentUser.branch_id}`, {
              params: {
                'filters[relationship_status][$eq]': 'active',
                'populate[seller][fields][0]': 'seller_name',
                'populate[seller][fields][1]': 'id',
                'pagination[pageSize]': 1000
              }
            });
            
            console.log('Branch relationships response:', relationshipsResponse.data);
            
            if (relationshipsResponse.data.data && relationshipsResponse.data.data.length > 0) {
              // Extract seller IDs from ACTIVE relationships only
              const sellerIds = relationshipsResponse.data.data
                .filter((rel: any) => rel.attributes?.relationship_status === 'active')
                .map((rel: any) => rel.attributes?.seller?.data?.id || rel.seller_id)
                .filter((id: any) => id);
              
              console.log('Seller IDs with active relationships:', sellerIds);
              
              if (sellerIds.length > 0) {
                // Now fetch medicines from these specific sellers
                // Since the API doesn't support $in filter, we'll fetch medicines for each seller individually
                let allMedicines: any[] = [];
                
                for (const sellerId of sellerIds) {
                  try {
                    const params = new URLSearchParams({
                      'seller_id': sellerId.toString(),
                      'populate[medicine][fields][0]': 'medicine_name',
                      'populate[medicine][fields][1]': 'id',
                      'populate[seller][fields][0]': 'seller_name',
                      'populate[seller][fields][1]': 'id',
                      'pagination[pageSize]': '1000',
                      ...(searchTerm && { 'search': searchTerm })
                    });
                    
                    const response = await apiClient.get(`/seller-medicines?${params}`);
                    if (response.data.data && response.data.data.length > 0) {
                      allMedicines = allMedicines.concat(response.data.data);
                    }
                  } catch (error) {
                    console.error(`Error fetching medicines for seller ${sellerId}:`, error);
                  }
                }
                
                console.log('Raw seller medicines for branch response:', allMedicines);
                
                // Transform the response to match the Medicine interface and filter out medicines with no stock
                const sellerMedicines = allMedicines
                  .map((sellerMedicine: any) => ({
                    medicine_id: sellerMedicine.attributes?.medicine?.data?.id || sellerMedicine.id,
                    medicine_name: sellerMedicine.attributes?.medicine?.data?.attributes?.medicine_name || 'Unknown Medicine',
                    unit_price: sellerMedicine.attributes?.unit_price || 0,
                    quantity: sellerMedicine.attributes?.seller_stock_quantity || 0,
                    seller_medicine_id: sellerMedicine.id,
                    seller_name: sellerMedicine.attributes?.seller?.data?.attributes?.seller_name || 'Unknown Seller',
                    seller_id: sellerMedicine.attributes?.seller?.data?.id
                  }))
                  .filter((medicine: any) => medicine.quantity > 0); // Only show medicines with stock > 0
                
                setMedicines(sellerMedicines);
                console.log('Fetched seller medicines for branch:', sellerMedicines);
              } else {
                console.log('No active seller relationships found for this branch');
                setMedicines([]);
              }
            } else {
              console.log('No active seller relationships found for this branch');
              setMedicines([]);
            }
          } catch (relationshipError) {
            console.error('Error fetching branch relationships:', relationshipError);
            setMedicines([]);
          }
        } else {
          console.error('No branch ID found in user data');
          setMedicines([]);
        }
      }
    } catch (error) {
      console.error('Error fetching medicines:', error);
      setMedicines([]);
    }
  }, [getUserRole]);

  const fetchBranches = useCallback(async () => {
    try {
      setBranchesLoading(true);
      const userRole = getUserRole();
      
      if (userRole === 'seller') {
        // For sellers, fetch only branches they have relationships with
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id) {
          const response = await apiClient.get(`/branch-seller-relationships/seller/${currentUser.id}`, {
            params: {
              'filters[relationship_status][$eq]': 'active',
              'populate[branch][fields][0]': 'branch_name',
              'populate[branch][fields][1]': 'id',
              'populate[branch][fields][2]': 'branch_code',
              'pagination[pageSize]': 1000
            }
          });
          
          console.log('Raw API response:', response.data);
          console.log('Response data structure:', response.data.data);
          
          // Transform the response to match the Branch interface
          const sellerBranches = response.data.data.map((relationship: any) => {
            console.log('Processing relationship:', relationship);
            console.log('Branch data:', relationship.attributes?.branch?.data);
            
            const branchData = {
              branch_id: relationship.attributes?.branch?.data?.id || relationship.branch_id,
              branch_name: relationship.attributes?.branch?.data?.attributes?.branch_name || 'Unknown Branch',
              branch_code: relationship.attributes?.branch?.data?.attributes?.branch_code || '',
              relationship_id: relationship.id // Include relationship ID for seller requests
            };
            
            console.log('Processed branch data:', branchData);
            return branchData;
          });
          
          setBranches(sellerBranches);
          console.log('Fetched seller branches:', sellerBranches);
        } else {
          console.error('No seller ID found in user data');
          setBranches([]);
        }
      } else {
        // For branch managers, fetch all branches
        const response = await apiClient.get('/branches');
        setBranches(response.data.data);
        console.log('Fetched all branches for branch manager:', response.data.data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  }, [getUserRole]);

  // fetchLowStockAlerts function removed - now handled by LowStockAlerts component

  // handleAutoGenerate function removed - now handled by LowStockAlerts component

  // Handle request status update for specific medicines in a row
  const handleStatusUpdate = async (medicines: any[], status: string, additionalData?: any) => {
    try {
      // Map status to endpoint as expected by the server
      const statusToEndpoint = {
        'approved': 'approve',
        'cancelled': 'reject', // 'cancelled' status uses 'reject' endpoint
        'shipped': 'ship',
        'delivered': 'deliver'
      };
      
      const endpoint = statusToEndpoint[status as keyof typeof statusToEndpoint];
      if (!endpoint) {
        message.error(`Invalid status: ${status}`);
        return;
      }
      
      // Update only the medicines in this specific row
      for (const medicine of medicines) {
        // Prepare the request body based on the endpoint
        let requestBody = {};
        
        switch (endpoint) {
          case 'approve':
            requestBody = {
              approved_quantity: additionalData?.approved_quantity || medicine.requested_quantity
            };
            break;
          case 'deliver':
            requestBody = {
              delivered_quantity: additionalData?.delivered_quantity || medicine.approved_quantity || medicine.requested_quantity,
              branch_medicine_id: additionalData?.branch_medicine_id
            };
            break;
          case 'reject':
            requestBody = {
              reason: additionalData?.reason || 'Cancelled by user'
            };
            break;
          case 'ship':
            // Ship action doesn't require additional parameters
            requestBody = {};
            break;
        }
        
        await apiClient.patch(`/inventory-requests/${medicine.id}/${endpoint}`, requestBody);
      }
      
      message.success(`Status updated to ${status} for ${medicines.length} medicine${medicines.length > 1 ? 's' : ''}`);
      fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
    } catch (error) {
      console.error('Error updating medicine status:', error);
      message.error('Failed to update medicine status');
    }
  };


  // Check if medicine exists in branch
  const checkMedicineInBranch = async (medicineId: number, branchId: number) => {
    try {
      const response = await apiClient.get(`/branch-medicines`, {
        params: {
          'filters[branch][id][$eq]': branchId,
          'filters[medicine][id][$eq]': medicineId,
          'pagination[pageSize]': 1
        }
      });
      return response.data.data.length > 0 ? response.data.data[0] : null;
    } catch (error) {
      console.error('Error checking medicine in branch:', error);
      return null;
    }
  };

  // Register medicine in branch
  const registerMedicineInBranch = async (medicine: any, branchId: number) => {
    try {
      const payload = {
        branch: branchId,
        medicine: medicine.medicine_id,
        seller_medicine: medicine.seller_medicine_id,
        current_stock: 0,
        unit_price: medicine.unit_price,
        status: 'active'
      };
      
      const response = await apiClient.post('/branch-medicines', payload);
      return response.data.data.id;
    } catch (error) {
      console.error('Error registering medicine in branch:', error);
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Clean up medicines data - remove max_quantity and ensure proper structure
      const cleanedMedicines = values.medicines.map((med: any) => ({
        medicine_id: med.medicine_id,
        requested_quantity: med.requested_quantity,
        unit_price: med.unit_price,
        seller_medicine_id: med.seller_medicine_id,
        seller_id: med.seller_id,
        seller_name: med.seller_name
      }));

      // Transform the form data to match the API format
      // Auto-determine request type: branch context = branch_to_seller, admin context = seller_to_branch
      const requestType = branchId ? 'branch_to_seller' : 'seller_to_branch';
      
      // Find the selected branch's relationship ID for seller requests
      const selectedBranchId = branchId || values.branch_id;
      const selectedBranch = branches.find(branch => branch.branch_id === selectedBranchId);
      const relationshipId = selectedBranch?.relationship_id;

      // Create individual requests for each medicine (API doesn't support bulk)
      const requests = cleanedMedicines.map((medicine: any, index: number) => {
        // Generate unique request_number for tracking
        const timestamp = Date.now();
        const randomSuffix = Math.random().toString(36).substr(2, 9);
        
        const requestData: any = {
          branch: selectedBranchId,
          requested_quantity: medicine.requested_quantity,
          quoted_unit_price: medicine.unit_price,
          request_type: requestType,
          notes: values.notes,
          requested_by: currentUser.id,
          // request_id will be generated by server sequentially
          request_number: `REQ-${timestamp}-${index}-${randomSuffix}` // Must be a string
        };

        // For seller requests, use seller_medicine instead of medicine
        if (requestType === 'seller_to_branch') {
          requestData.seller_medicine = medicine.seller_medicine_id || medicine.medicine_id;
          // Add seller ID
          requestData.seller = currentUser.id;
          // Add relationship ID from selected branch
          if (relationshipId) {
            requestData.relationship = relationshipId;
          }
        } else {
          // For branch requests, use seller_medicine_id and check if medicine exists in branch
          if (medicine.seller_medicine_id) {
            requestData.seller_medicine = medicine.seller_medicine_id;
          }
          requestData.medicine = medicine.medicine_id;
          // Add seller information for branch requests
          if (medicine.seller_id) {
            requestData.seller = medicine.seller_id;
          }
        }

        return requestData;
      });

      console.log('Creating inventory requests:', {
        requests,
        branchId,
        requestType,
        userRole: getUserRole()
      });

      // For branch requests, check if medicines exist in branch and register if needed
      if (requestType === 'branch_to_seller' && branchId) {
        for (const medicine of cleanedMedicines) {
          try {
            const existingMedicine = await checkMedicineInBranch(medicine.medicine_id, branchId);
            if (!existingMedicine) {
              console.log(`Medicine ${medicine.medicine_name} not found in branch, registering...`);
              await registerMedicineInBranch(medicine, branchId);
              console.log(`Medicine ${medicine.medicine_name} registered in branch successfully`);
            }
          } catch (medicineError) {
            console.error(`Error handling medicine ${medicine.medicine_name}:`, medicineError);
            // Continue with other medicines even if one fails
            message.warning(`Failed to register medicine ${medicine.medicine_name} in branch. Continuing with request creation.`);
          }
        }
      }

      // Create requests one by one
      const responses = [];
      for (const requestData of requests) {
        const response = await apiClient.post('/inventory-requests', requestData);
        responses.push(response);
      }
      
      console.log('Request creation responses:', responses.map(r => r.data));
      
      const requestTypeDisplay = branchId ? 'branch' : 'seller direct';
      message.success(`${responses.length} ${requestTypeDisplay} requests created successfully`);
      
      setCreateModalVisible(false);
      setMedicineSearchTerm('');
      form.resetFields();
      
      // Refresh the requests list with error handling
      try {
        await fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
      } catch (refreshError) {
        console.error('Error refreshing requests after creation:', refreshError);
        // Don't show error dialog for refresh failure, just log it
        // The request was created successfully
      }
    } catch (error: any) {
      console.error('Error creating request:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        request: error.request,
        config: error.config,
        stack: error.stack
      });
      
      // Handle network errors
      if (!error.response) {
        Modal.error({
          title: '❌ Network Error',
          content: (
            <div>
              <p style={{ marginBottom: '16px', fontSize: '16px' }}>
                Unable to connect to the server. Please check your internet connection.
              </p>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Please try again in a few moments.
              </p>
            </div>
          ),
          okText: 'OK',
          width: 500,
          centered: true
        });
        return;
      }
      
      // Check if it's an insufficient stock error
      if (error.response?.data?.error?.includes('Insufficient stock')) {
        const errorMessage = error.response.data.error;
        Modal.error({
          title: '❌ Insufficient Stock',
          content: (
            <div>
              <p style={{ marginBottom: '16px', fontSize: '16px' }}>
                {errorMessage}
              </p>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Please reduce the requested quantity or select a different medicine.
              </p>
            </div>
          ),
          okText: 'OK',
          width: 500,
          centered: true
        });
      } else if (error.response?.data?.error?.includes('Medicine with ID')) {
        Modal.error({
          title: '❌ Medicine Not Found',
          content: (
            <div>
              <p style={{ marginBottom: '16px', fontSize: '16px' }}>
                {error.response.data.error}
              </p>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Please refresh the page and try again.
              </p>
            </div>
          ),
          okText: 'OK',
          width: 500,
          centered: true
        });
      } else {
        Modal.error({
          title: '❌ Request Failed',
          content: (
            <div>
              <p style={{ marginBottom: '16px', fontSize: '16px' }}>
                {error.response?.data?.error || 'Failed to create inventory request'}
              </p>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Please check your input and try again.
              </p>
            </div>
          ),
          okText: 'OK',
          width: 500,
          centered: true
        });
      }
    }
  };

  // Handle export functionality
  const handleExport = () => {
    try {
      // Create CSV content
      const headers = [
        'Request Number',
        'Branch Name',
        'Medicine Name',
        'Requested Quantity',
        'Approved Quantity',
        'Delivered Quantity',
        'Status',
        'Request Type',
        'Unit Price',
        'Total Cost',
        'Request Date',
        'Manager Name',
        'Notes'
      ];

      // Flatten grouped requests for export
      const exportData = requests.flatMap(request => {
        const medicines = request.medicines || [request];
        return medicines.map(medicine => [
          request.request_number,
          `"${request.branch_name}"`,
          `"${medicine.medicine_name}"`,
          medicine.requested_quantity,
          medicine.approved_quantity,
          medicine.delivered_quantity,
          request.status,
          request.request_type,
          medicine.unit_price,
          (Number(medicine.unit_price) * Number(medicine.requested_quantity)).toFixed(2),
          new Date(request.request_date).toLocaleDateString(),
          `"${request.manager_name || 'N/A'}"`,
          `"${request.notes || ''}"`
        ].join(','));
      });

      const csvContent = [
        headers.join(','),
        ...exportData
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory_requests_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('Inventory requests exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export inventory requests');
    }
  };

  // handleBulkCreate function removed - now handled by LowStockAlerts component

  // Effects
  useEffect(() => {
    // Debug user authentication on component load
    console.log('=== INVENTORY REQUESTS COMPONENT LOADED ===');
    console.log('Component props - branchId:', branchId);
    console.log('Current user role:', getUserRole());
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('Current user data:', userData);
    console.log('User branch from localStorage:', userData.branch);
    console.log('User branch_id from localStorage:', userData.branch_id); // fallback
    console.log('Expected branch_id (from props):', branchId);
    console.log('Is branchId provided?', !!branchId);
    console.log('Is userData.branch available?', !!userData.branch);
    console.log('Is userData.branch_id available?', !!userData.branch_id); // fallback
    console.log('Should show branch-specific requests?', !!(branchId || userData.branch || userData.branch_id));
    
    // Check if user is accessing the wrong route
    if ((userData.branch || userData.branch_id) && !branchId) {
      console.log('⚠️ WARNING: Branch user accessing main admin route');
      console.log('⚠️ User should access /branch/inventory-requests instead of /main/inventory-requests');
      console.log('⚠️ This may cause incorrect filtering behavior');
    }
    
    fetchRequests();
    fetchMedicines();
    fetchBranches();
  }, [branchId, fetchRequests, fetchMedicines, fetchBranches, getUserRole]);

  // Auto-refresh every 30 seconds to catch new requests
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [fetchRequests, pagination?.current, pagination?.pageSize]);
  
  // Workflow action functions
  const handleWorkflowAction = async (requestId: number, action: string, userRole: string, additionalData?: any) => {
    try {
      console.log('=== WORKFLOW ACTION DEBUG ===');
      console.log('Performing workflow action:', {
        requestId,
        action,
        userRole,
        additionalData
      });
      console.log('API endpoint will be:', `/inventory-requests/${requestId}/${action}`);
      
      // Prepare the request body based on the action
      let requestBody = {};
      
      switch (action) {
        case 'approve':
          requestBody = {
            approved_quantity: additionalData?.approved_quantity || 0
          };
          break;
        case 'deliver':
          requestBody = {
            delivered_quantity: additionalData?.delivered_quantity || 0,
            branch_medicine_id: additionalData?.branch_medicine_id
          };
          break;
        case 'reject':
          requestBody = {
            reason: additionalData?.reason || 'Rejected by user'
          };
          break;
        case 'ship':
          // Ship action doesn't require additional parameters
          requestBody = {};
          break;
      }
      
      const response = await apiClient.patch(`/inventory-requests/${requestId}/${action}`, requestBody);
      
      console.log('Workflow action response:', response.data);
      
      if (response.data.success || response.status === 200) {
        message.success(response.data.message || `Request ${action}d successfully`);
        console.log('Refreshing requests after successful action...');
        
        // Handle seller medicine stock update and branch medicine creation for delivery actions
        if (action === 'deliver') {
          try {
            console.log('Processing delivery actions...');
            // Get the inventory request details to find seller_medicine_id and branch info
            const requestDetails = await apiClient.get(`/inventory-requests/${requestId}`);
            const inventoryRequest = requestDetails.data.data;
            
            console.log('Inventory request details:', inventoryRequest);
            
            // 1. Update seller medicine stock if seller_medicine_id exists
            if (inventoryRequest.seller_medicine_id && additionalData?.delivered_quantity > 0) {
              console.log('Found seller medicine ID:', inventoryRequest.seller_medicine_id);
              
              // Get current seller medicine stock
              const sellerMedicineResponse = await apiClient.get(`/seller-medicines/${inventoryRequest.seller_medicine_id}`);
              const currentStock = sellerMedicineResponse.data.data.attributes.seller_stock_quantity;
              const newStock = Math.max(0, currentStock - additionalData.delivered_quantity);
              
              console.log(`Updating seller medicine stock from ${currentStock} to ${newStock}`);
              
              // Update seller medicine stock
              await apiClient.put(`/seller-medicines/${inventoryRequest.seller_medicine_id}`, {
                seller_stock_quantity: newStock
              });
              
              console.log('Seller medicine stock updated successfully');
            }
            
            // 2. Handle branch medicine stock (create new or update existing)
            if (inventoryRequest.seller_medicine_id && inventoryRequest.branch_id) {
              // Get seller medicine details to find the medicine ID
              const sellerMedicineResponse = await apiClient.get(`/seller-medicines/${inventoryRequest.seller_medicine_id}`);
              const sellerMedicine = sellerMedicineResponse.data.data;
              const medicineId = sellerMedicine.attributes.medicine.data.id;
              
              // Check if branch medicine already exists for this branch + medicine combination
              try {
                const existingBranchMedicineResponse = await apiClient.get('/branch-medicines', {
                  params: {
                    branch_id: inventoryRequest.branch_id,
                    medicine_id: medicineId,
                    pageSize: 1
                  }
                });
                
                const existingBranchMedicines = existingBranchMedicineResponse.data.data;
                
                if (existingBranchMedicines.length > 0) {
                  // Branch medicine exists - update stock
                  const existingBranchMedicine = existingBranchMedicines[0];
                  const currentBranchStock = existingBranchMedicine.attributes.current_stock;
                  const newBranchStock = currentBranchStock + (additionalData.delivered_quantity || 0);
                  
                  console.log(`Updating existing branch medicine ${existingBranchMedicine.id} stock from ${currentBranchStock} to ${newBranchStock}`);
                  
                  // Update existing branch medicine stock
                  await apiClient.put(`/branch-medicines/${existingBranchMedicine.id}`, {
                    current_stock: newBranchStock,
                    last_restocked_date: new Date().toISOString()
                  });
                  
                  // Update inventory request with the existing branch_medicine_id
                  await apiClient.put(`/inventory-requests/${requestId}`, {
                    branch_medicine_id: existingBranchMedicine.id
                  });
                  
                  console.log('Updated existing branch medicine stock and linked to inventory request');
                } else {
                  // Branch medicine doesn't exist - create new one
                  console.log('Creating new branch medicine for delivered seller medicine...');
                  
                  const branchMedicineData = {
                    branch: inventoryRequest.branch_id,
                    medicine: medicineId,
                    seller_medicine: inventoryRequest.seller_medicine_id,
                    current_stock: additionalData.delivered_quantity || 0,
                    unit_price: inventoryRequest.quoted_unit_price || sellerMedicine.attributes.unit_price,
                    status: 'active',
                    notes: `Created from delivery of inventory request ${inventoryRequest.request_number}`
                  };
                  
                  console.log('Creating branch medicine with data:', branchMedicineData);
                  
                  try {
                    // Create new branch medicine
                    console.log('Attempting to create branch medicine with data:', JSON.stringify(branchMedicineData, null, 2));
                    const branchMedicineResponse = await apiClient.post('/branch-medicines', branchMedicineData);
                    console.log('Branch medicine creation response:', branchMedicineResponse.data);
                    
                    if (branchMedicineResponse.data && branchMedicineResponse.data.status === 'success' && branchMedicineResponse.data.data) {
                      const newBranchMedicineId = branchMedicineResponse.data.data.id;
                      console.log('Created new branch medicine with ID:', newBranchMedicineId);
                      
                      // Update inventory request with the new branch_medicine_id
                      await apiClient.put(`/inventory-requests/${requestId}`, {
                        branch_medicine_id: newBranchMedicineId
                      });
                      
                      console.log('Updated inventory request with new branch_medicine_id');
                    } else {
                      console.error('Invalid response structure from branch medicine creation:', branchMedicineResponse.data);
                      throw new Error('Invalid response from branch medicine creation API');
                    }
                  } catch (createError: any) {
                    console.error('Error creating branch medicine:', createError);
                    console.error('Error details:', createError.response?.data);
                    throw createError;
                  }
                }
              } catch (branchMedicineError) {
                console.error('Error handling branch medicine:', branchMedicineError);
              }
            }
            
          } catch (deliveryError) {
            console.error('Error processing delivery actions:', deliveryError);
            // Don't fail the delivery if these operations fail, just log the error
            message.warning('Delivery completed but some operations failed. Please check manually.');
          }
        }
        
        // Force refresh the requests
        await fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
        // Refresh low stock alerts for delivery actions (when inventory is actually updated)
        if (action === 'deliver') {
          // Low stock alerts now handled by LowStockAlerts component
        }
        console.log('Requests refreshed successfully');
      } else {
        console.error('API returned success: false', response.data);
      }
    } catch (error: any) {
      console.error('Error performing workflow action:', error);
      const errorMessage = error.response?.data?.error || 'Failed to perform action';
      message.error(errorMessage);
    }
  };

  // Handle bulk workflow actions for grouped requests
  const handleBulkWorkflowAction = async (requestNumber: string, action: string, userRole: string, additionalData?: any) => {
    try {
      console.log('Performing bulk workflow action:', {
        requestNumber,
        action,
        userRole,
        additionalData
      });
      
      // Find all request IDs with the same request_number
      const allRequests = await apiClient.get(`/inventory-requests?search=${requestNumber}`);
      const matchingRequests = allRequests.data.data.filter((req: any) => req.request_number === requestNumber);
      
      console.log('Found matching requests for bulk action:', matchingRequests.map((r: any) => ({
        id: r.request_id,
        status: r.status,
        medicine: r.medicine_name
      })));
      
      // Filter requests that can actually perform this action based on their current status
      const validRequests = matchingRequests.filter((req: any) => {
        // Define valid status transitions
        const validTransitions: Record<string, string[]> = {
          'approve': ['pending'],
          'reject': ['pending'],
          'ship': ['approved'],
          'deliver': ['shipped']
        };
        
        const allowedStatuses = validTransitions[action] || [];
        return allowedStatuses.includes(req.status);
      });
      
      console.log('Valid requests for action:', validRequests.map((r: any) => ({
        id: r.request_id,
        status: r.status,
        medicine: r.medicine_name
      })));
      
      if (validRequests.length === 0) {
        // For delivery action, check if we can auto-ship approved medicines first
        if (action === 'deliver') {
          const approvedRequests = matchingRequests.filter((req: any) => req.status === 'approved');
          if (approvedRequests.length > 0) {
            // Auto-ship approved medicines first, then deliver
            console.log('Auto-shipping approved medicines before delivery:', approvedRequests.map((r: any) => ({
              id: r.request_id,
              medicine: r.medicine_name
            })));
            
            try {
              // First ship all approved medicines
              const shipPromises = approvedRequests.map((req: any) => 
                apiClient.patch(`/inventory-requests/${req.id}/ship`)
              );
              
              await Promise.all(shipPromises);
              console.log('Successfully shipped approved medicines');
              
              // Refresh the data to get updated statuses
              const updatedRequests = await apiClient.get(`/inventory-requests?search=${requestNumber}`);
              const updatedMatchingRequests = updatedRequests.data.data.filter((req: any) => req.request_number === requestNumber);
              
              // Now deliver all shipped medicines
              const shippedRequests = updatedMatchingRequests.filter((req: any) => req.status === 'shipped');
              const deliverPromises = shippedRequests.map((req: any) => 
                apiClient.patch(`/inventory-requests/${req.id}/deliver`, {
                  delivered_quantity: additionalData?.delivered_quantity || req.approved_quantity || req.requested_quantity,
                  branch_medicine_id: additionalData?.branch_medicine_id
                })
              );
              
              const deliverResponses = await Promise.all(deliverPromises);
              const successCount = deliverResponses.filter(res => res.data.success).length;
              
              // Update seller medicine stock and create branch medicines for each successfully delivered request
              const deliveredRequests = shippedRequests.filter((req: any, index: number) => deliverResponses[index]?.data?.success);
              for (const req of deliveredRequests) {
                try {
                  const deliveredQuantity = additionalData?.delivered_quantity || req.approved_quantity || req.requested_quantity;
                  
                  // 1. Update seller medicine stock if seller_medicine_id exists
                  if (req.seller_medicine_id && deliveredQuantity > 0) {
                    // Get current seller medicine stock
                    const sellerMedicineResponse = await apiClient.get(`/seller-medicines/${req.seller_medicine_id}`);
                    const currentStock = sellerMedicineResponse.data.data.attributes.seller_stock_quantity;
                    const newStock = Math.max(0, currentStock - deliveredQuantity);
                    
                    // Update seller medicine stock
                    await apiClient.put(`/seller-medicines/${req.seller_medicine_id}`, {
                      seller_stock_quantity: newStock
                    });
                    
                    console.log(`Updated seller medicine ${req.seller_medicine_id} stock from ${currentStock} to ${newStock}`);
                  }
                  
                  // 2. Handle branch medicine stock (create new or update existing)
                  if (req.seller_medicine_id && req.branch_id) {
                    // Get seller medicine details to find the medicine ID
                    const sellerMedicineResponse = await apiClient.get(`/seller-medicines/${req.seller_medicine_id}`);
                    const sellerMedicine = sellerMedicineResponse.data.data;
                    const medicineId = sellerMedicine.attributes.medicine.data.id;
                    
                    // Check if branch medicine already exists for this branch + medicine combination
                    try {
                      const existingBranchMedicineResponse = await apiClient.get('/branch-medicines', {
                        params: {
                          branch_id: req.branch_id,
                          medicine_id: medicineId,
                          pageSize: 1
                        }
                      });
                      
                      const existingBranchMedicines = existingBranchMedicineResponse.data.data;
                      
                      if (existingBranchMedicines.length > 0) {
                        // Branch medicine exists - update stock
                        const existingBranchMedicine = existingBranchMedicines[0];
                        const currentBranchStock = existingBranchMedicine.attributes.current_stock;
                        const newBranchStock = currentBranchStock + (deliveredQuantity || 0);
                        
                        console.log(`Updating existing branch medicine ${existingBranchMedicine.id} stock from ${currentBranchStock} to ${newBranchStock}`);
                        
                        // Update existing branch medicine stock
                        await apiClient.put(`/branch-medicines/${existingBranchMedicine.id}`, {
                          current_stock: newBranchStock,
                          last_restocked_date: new Date().toISOString()
                        });
                        
                        // Update inventory request with the existing branch_medicine_id
                        await apiClient.put(`/inventory-requests/${req.id}`, {
                          branch_medicine_id: existingBranchMedicine.id
                        });
                        
                        console.log(`Updated existing branch medicine stock and linked to inventory request ${req.id}`);
                      } else {
                        // Branch medicine doesn't exist - create new one
                        console.log(`Creating new branch medicine for request ${req.id}...`);
                        
                        const branchMedicineData = {
                          branch: req.branch_id,
                          medicine: medicineId,
                          seller_medicine: req.seller_medicine_id,
                          current_stock: deliveredQuantity || 0,
                          unit_price: req.quoted_unit_price || sellerMedicine.attributes.unit_price,
                          status: 'active',
                          notes: `Created from delivery of inventory request ${req.request_number}`
                        };
                        
                        try {
                          // Create new branch medicine
                          console.log(`Attempting to create branch medicine for request ${req.id} with data:`, JSON.stringify(branchMedicineData, null, 2));
                          const branchMedicineResponse = await apiClient.post('/branch-medicines', branchMedicineData);
                          console.log(`Branch medicine creation response for request ${req.id}:`, branchMedicineResponse.data);
                          
                          if (branchMedicineResponse.data && branchMedicineResponse.data.status === 'success' && branchMedicineResponse.data.data) {
                            const newBranchMedicineId = branchMedicineResponse.data.data.id;
                            console.log(`Created new branch medicine with ID: ${newBranchMedicineId}`);
                            
                            // Update inventory request with the new branch_medicine_id
                            await apiClient.put(`/inventory-requests/${req.id}`, {
                              branch_medicine_id: newBranchMedicineId
                            });
                            
                            console.log(`Updated inventory request ${req.id} with new branch_medicine_id`);
                          } else {
                            console.error(`Invalid response structure from branch medicine creation for request ${req.id}:`, branchMedicineResponse.data);
                            throw new Error(`Invalid response from branch medicine creation API for request ${req.id}`);
                          }
                        } catch (createError: any) {
                          console.error(`Error creating branch medicine for request ${req.id}:`, createError);
                          console.error('Error details:', createError.response?.data);
                          throw createError;
                        }
                      }
                    } catch (branchMedicineError) {
                      console.error(`Error handling branch medicine for request ${req.id}:`, branchMedicineError);
                    }
                  }
                } catch (stockUpdateError) {
                  console.error(`Error processing delivery actions for request ${req.id}:`, stockUpdateError);
                }
              }
              
              message.success(`Successfully shipped ${approvedRequests.length} medicine(s) and delivered ${successCount} request(s)`);
              await fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
              // Low stock alerts now handled by LowStockAlerts component // Refresh low stock alerts after delivery
              return;
            } catch (error: any) {
              console.error('Error in auto-ship and deliver:', error);
              message.error('Failed to auto-ship and deliver medicines');
              return;
            }
          }
        }
        message.warning(`No requests can be ${action}ed at this time. All requests are in incompatible statuses.`);
        return;
      }
      
      // For delivery action, use the bulk delivery endpoint
      if (action === 'deliver') {
        try {
          const response = await apiClient.put(`/inventory-requests/bulk-deliver/${requestNumber}`, {});
          
          if (response.data.success) {
            // Update seller medicine stock and create branch medicines for delivered requests
            for (const req of validRequests) {
              try {
                const deliveredQuantity = req.approved_quantity || req.requested_quantity;
                
                // 1. Update seller medicine stock if seller_medicine_id exists
                if (req.seller_medicine_id && deliveredQuantity > 0) {
                  // Get current seller medicine stock
                  const sellerMedicineResponse = await apiClient.get(`/seller-medicines/${req.seller_medicine_id}`);
                  const currentStock = sellerMedicineResponse.data.data.attributes.seller_stock_quantity;
                  const newStock = Math.max(0, currentStock - deliveredQuantity);
                  
                  // Update seller medicine stock
                  await apiClient.put(`/seller-medicines/${req.seller_medicine_id}`, {
                    seller_stock_quantity: newStock
                  });
                  
                  console.log(`Updated seller medicine ${req.seller_medicine_id} stock from ${currentStock} to ${newStock}`);
                }
                
                // 2. Handle branch medicine stock (create new or update existing)
                if (req.seller_medicine_id && req.branch_id) {
                  // Get seller medicine details to find the medicine ID
                  const sellerMedicineResponse = await apiClient.get(`/seller-medicines/${req.seller_medicine_id}`);
                  const sellerMedicine = sellerMedicineResponse.data.data;
                  const medicineId = sellerMedicine.attributes.medicine.data.id;
                  
                  // Check if branch medicine already exists for this branch + medicine combination
                  try {
                    const existingBranchMedicineResponse = await apiClient.get('/branch-medicines', {
                      params: {
                        branch_id: req.branch_id,
                        medicine_id: medicineId,
                        pageSize: 1
                      }
                    });
                    
                    const existingBranchMedicines = existingBranchMedicineResponse.data.data;
                    
                    if (existingBranchMedicines.length > 0) {
                      // Branch medicine exists - update stock
                      const existingBranchMedicine = existingBranchMedicines[0];
                      const currentBranchStock = existingBranchMedicine.attributes.current_stock;
                      const newBranchStock = currentBranchStock + (deliveredQuantity || 0);
                      
                      console.log(`Updating existing branch medicine ${existingBranchMedicine.id} stock from ${currentBranchStock} to ${newBranchStock}`);
                      
                      // Update existing branch medicine stock
                      await apiClient.put(`/branch-medicines/${existingBranchMedicine.id}`, {
                        current_stock: newBranchStock,
                        last_restocked_date: new Date().toISOString()
                      });
                      
                      // Update inventory request with the existing branch_medicine_id
                      await apiClient.put(`/inventory-requests/${req.id}`, {
                        branch_medicine_id: existingBranchMedicine.id
                      });
                      
                      console.log(`Updated existing branch medicine stock and linked to inventory request ${req.id}`);
                    } else {
                      // Branch medicine doesn't exist - create new one
                      console.log(`Creating new branch medicine for request ${req.id}...`);
                      
                      const branchMedicineData = {
                        branch: req.branch_id,
                        medicine: medicineId,
                        seller_medicine: req.seller_medicine_id,
                        current_stock: deliveredQuantity || 0,
                        unit_price: req.quoted_unit_price || sellerMedicine.attributes.unit_price,
                        status: 'active',
                        notes: `Created from delivery of inventory request ${req.request_number}`
                      };
                      
                      try {
                        // Create new branch medicine
                        console.log(`Attempting to create branch medicine for request ${req.id} with data:`, JSON.stringify(branchMedicineData, null, 2));
                        const branchMedicineResponse = await apiClient.post('/branch-medicines', branchMedicineData);
                        console.log(`Branch medicine creation response for request ${req.id}:`, branchMedicineResponse.data);
                        
                        if (branchMedicineResponse.data && branchMedicineResponse.data.status === 'success' && branchMedicineResponse.data.data) {
                          const newBranchMedicineId = branchMedicineResponse.data.data.id;
                          console.log(`Created new branch medicine with ID: ${newBranchMedicineId}`);
                          
                          // Update inventory request with the new branch_medicine_id
                          await apiClient.put(`/inventory-requests/${req.id}`, {
                            branch_medicine_id: newBranchMedicineId
                          });
                          
                          console.log(`Updated inventory request ${req.id} with new branch_medicine_id`);
                        } else {
                          console.error(`Invalid response structure from branch medicine creation for request ${req.id}:`, branchMedicineResponse.data);
                          throw new Error(`Invalid response from branch medicine creation API for request ${req.id}`);
                        }
                      } catch (createError: any) {
                        console.error(`Error creating branch medicine for request ${req.id}:`, createError);
                        console.error('Error details:', createError.response?.data);
                        throw createError;
                      }
                    }
                  } catch (branchMedicineError) {
                    console.error(`Error handling branch medicine for request ${req.id}:`, branchMedicineError);
                  }
                }
              } catch (stockUpdateError) {
                console.error(`Error processing delivery actions for request ${req.id}:`, stockUpdateError);
              }
            }
            
            message.success(response.data.message);
            await fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
            // Low stock alerts now handled by LowStockAlerts component // Refresh low stock alerts after bulk delivery
          } else {
            message.error(response.data.error || 'Failed to deliver requests');
          }
        } catch (error: any) {
          console.error('Error in bulk delivery:', error);
          const errorMessage = error.response?.data?.error || 'Failed to perform bulk delivery';
          message.error(errorMessage);
        }
      } else {
        // For other actions, use the individual request approach
        // Don't pass global additionalData for bulk actions - each request should use its own quantities
        const promises = validRequests.map((req: any) => 
          apiClient.patch(`/inventory-requests/${req.id}/${action}`)
        );
        
        const responses = await Promise.all(promises);
        const successCount = responses.filter(res => res.data.success).length;
        
        if (successCount === validRequests.length) {
          message.success(`Successfully ${action}ed ${successCount} request${successCount > 1 ? 's' : ''}`);
          await fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
          // Only refresh low stock alerts for delivery actions (when inventory is actually updated)
          if (action === 'deliver') {
            // Low stock alerts now handled by LowStockAlerts component
          }
        } else {
          message.warning(`Partially completed: ${action}ed ${successCount} out of ${validRequests.length} valid requests`);
          await fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
          // Only refresh low stock alerts for delivery actions (when inventory is actually updated)
          if (action === 'deliver') {
            // Low stock alerts now handled by LowStockAlerts component
          }
        }
      }
    } catch (error: any) {
      console.error('Error performing bulk workflow action:', error);
      const errorMessage = error.response?.data?.error || 'Failed to perform bulk action';
      message.error(errorMessage);
    }
  };

  // Get available actions for a request based on status and type
  const getAvailableActions = (status: string, requestType: string, medicines?: any[]): string[] => {
    const userRole = getUserRole();
    
    console.log('Getting available actions:', {
      status,
      requestType,
      userRole,
      medicinesCount: medicines?.length,
      branchId
    });
    
    const workflows: Record<string, Record<string, Record<string, string[]>>> = {
      // Legacy request types (for backward compatibility)
      'seller_direct': {
        'pending': {
          'buyer': [],
          'seller': [],
          'branch': ['approve', 'reject']
        },
        'approved': {
          'buyer': [],
          'seller': ['ship'],
          'branch': ['deliver']  // Allow branch to mark as delivered
        },
        'shipped': {
          'buyer': [],
          'seller': [],
          'branch': ['deliver']
        },
        'delivered': {},
        'cancelled': {}
      },
      'branch_request': {
        'pending': {
          'buyer': [],
          'seller': ['approve', 'reject'],
          'branch': []
        },
        'approved': {
          'buyer': [],
          'seller': ['ship'],
          'branch': []
        },
        'shipped': {
          'buyer': [],
          'seller': [],
          'branch': ['deliver']
        },
        'delivered': {},
        'cancelled': {}
      },
      // Actual database request types
      'seller_to_branch': {
        'pending': {
          'buyer': [],
          'seller': [],
          'branch': ['approve', 'reject']
        },
        'approved': {
          'buyer': [],
          'seller': ['ship'],
          'branch': ['deliver']  // Allow branch to mark as delivered
        },
        'shipped': {
          'buyer': [],
          'seller': [],
          'branch': ['deliver']
        },
        'delivered': {},
        'cancelled': {}
      },
      'branch_to_seller': {
        'pending': {
          'buyer': [],
          'seller': ['approve', 'reject'],
          'branch': []
        },
        'approved': {
          'buyer': [],
          'seller': ['ship'],
          'branch': []
        },
        'shipped': {
          'buyer': [],
          'seller': [],
          'branch': ['deliver']
        },
        'delivered': {},
        'cancelled': {}
      }
    };

    // For bulk requests, check if any medicine can perform the action
    if (medicines && medicines.length > 1) {
      const allActions = new Set<string>();
      
      // Check each medicine's status and collect all possible actions
      medicines.forEach(medicine => {
        const medicineStatus = medicine.status || status;
        const medicineActions = workflows[requestType]?.[medicineStatus]?.[userRole] || [];
        medicineActions.forEach(action => allActions.add(action));
      });
      
      const availableActions = Array.from(allActions);
      console.log('Available actions for bulk request:', availableActions);
      return availableActions;
    }

    const availableActions = workflows[requestType]?.[status]?.[userRole] || [];
    console.log('Available actions determined:', {
      availableActions,
      workflowPath: `${requestType}.${status}.${userRole}`,
      workflowExists: !!workflows[requestType]?.[status]?.[userRole],
      requestType,
      status,
      userRole
    });
    return availableActions;
  };

  // Fetch medicines when search term changes
  useEffect(() => {
    if (createModalVisible) {
      fetchMedicines(debouncedMedicineSearch);
    }
  }, [debouncedMedicineSearch, createModalVisible, fetchMedicines]);

  useEffect(() => {
    fetchRequests(pagination?.current || 1, pagination?.pageSize || 10);
  }, [debouncedSearch, filters.branch_id, filters.medicine_id, filters.status, filters.request_type, branchId, fetchRequests]); // eslint-disable-line react-hooks/exhaustive-deps

  // Table columns
  const columns = [
    {
      title: 'Request #',
      dataIndex: 'request_number',
      key: 'request_number',
      width: 120,
      render: (text: string) => (
        <Button type="link" onClick={() => {
          setSelectedRequest(requests.find(r => r.request_number === text) || null);
          setDetailsModalVisible(true);
        }}>
          {text}
        </Button>
      )
    },
    {
      title: 'Branch',
      dataIndex: 'branch_name',
      key: 'branch_name',
      width: 180,
      render: (text: string, record: InventoryRequest) => (
        <div>
          <div><strong>{text}</strong></div>
          <small className="text-muted">{record.branch_code}</small>
          {record.branch_location && (
            <div className="text-muted" style={{ fontSize: '11px' }}>
              📍 {record.branch_location}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Status & Timeline',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string, record: InventoryRequest) => {
        const statusConfig = {
          pending: { color: 'orange', icon: <ClockCircleOutlined /> },
          approved: { color: 'blue', icon: <CheckCircleOutlined /> },
          shipped: { color: 'purple', icon: <TruckOutlined /> },
          delivered: { color: 'green', icon: <CheckCircleOutlined /> },
          cancelled: { color: 'red', icon: <ExclamationCircleOutlined /> }
        };
        
        const safeStatus = status || 'unknown';
        const config = statusConfig[safeStatus as keyof typeof statusConfig] || { 
          color: 'default', 
          icon: <ClockCircleOutlined /> 
        };
        
        // Highlight pending seller_direct requests for branch users
        const userRole = getUserRole();
        const shouldHighlight = userRole === 'branch' && 
                               record.request_type === 'seller_direct' && 
                               safeStatus === 'pending';
        
        return (
          <div>
            <Tag 
              color={config.color} 
              icon={config.icon}
              style={{
                border: shouldHighlight ? '2px solid #1890ff' : undefined,
                fontWeight: shouldHighlight ? 'bold' : undefined
              }}
            >
              {record.status_display || safeStatus.toUpperCase()}
            </Tag>
            {record.days_since_request !== undefined && (
              <div className="text-muted" style={{ fontSize: '11px' }}>
                {Math.round(record.days_since_request)} days ago
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Medicines',
      dataIndex: 'medicines',
      key: 'medicines',
      width: 200,
      render: (medicines: any[], record: any) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
            Total: {record.total_requested_quantity} units
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {medicines.slice(0, 2).map((med, index) => (
              <div key={index}>
                {med.medicine_name} ({med.requested_quantity})
              </div>
            ))}
            {medicines.length > 2 && (
              <div>+{medicines.length - 2} more...</div>
            )}
          </div>
        </div>
      )
     },
     {
       title: 'Total Cost',
       dataIndex: 'total_value',
       key: 'total_value',
       width: 120,
       render: (value: number, record: any) => (
         <div style={{ textAlign: 'right' }}>
           <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '14px' }}>
             ₹{value.toFixed(2)}
           </div>
           <div style={{ fontSize: '11px', color: '#666' }}>
             {record.total_medicines} item{record.total_medicines !== 1 ? 's' : ''}
           </div>
         </div>
       )
     },
    {
      title: 'Type',
      dataIndex: 'request_type',
      key: 'request_type',
      width: 120,
      render: (type: string) => {
        const safeType = type || 'unknown';
        return (
          <Tag color={getRequestTypeColor(safeType)}>
            {getRequestTypeDisplay(safeType)}
          </Tag>
        );
      }
    },
    {
      title: 'Request Date',
      dataIndex: 'request_date',
      key: 'request_date',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (record: InventoryRequest) => {
        const userRole = getUserRole();
        const safeStatus = record.status || 'unknown';
        const safeRequestType = record.request_type || 'unknown';
        const availableActions = getAvailableActions(safeStatus, safeRequestType, record.medicines);
        
        // Debug logging for branch users
        if (userRole === 'branch' && (safeRequestType === 'seller_direct' || safeRequestType === 'seller_to_branch')) {
          console.log('=== BRANCH USER DEBUG ===');
          console.log('Branch user viewing seller request:', {
            requestId: record.request_id,
            requestNumber: record.request_number,
            status: safeStatus,
            requestType: safeRequestType,
            userRole,
            availableActions,
            branchId,
            recordData: {
              request_type: record.request_type,
              status: record.status,
              medicines: record.medicines?.length
            }
          });
          console.log('=== END BRANCH USER DEBUG ===');
        }

        // Debug logging for seller users
        if (userRole === 'seller' && (safeRequestType === 'branch_request' || safeRequestType === 'branch_to_seller')) {
          console.log('=== SELLER USER DEBUG ===');
          console.log('Seller user viewing branch request:', {
            requestId: record.request_id,
            requestNumber: record.request_number,
            status: safeStatus,
            requestType: safeRequestType,
            userRole,
            availableActions,
            recordData: {
              request_type: record.request_type,
              status: record.status,
              medicines: record.medicines?.length
            }
          });
          console.log('=== END SELLER USER DEBUG ===');
        }
        
        // Get the request ID - use bulk_request_id if available, otherwise find first medicine's id
        const getRequestId = (): number => {
          console.log('=== GET REQUEST ID DEBUG ===');
          console.log('Record structure:', {
            id: record.id,
            request_id: record.request_id,
            bulk_request_id: record.bulk_request_id,
            medicines: record.medicines?.length,
            firstMedicineId: record.medicines?.[0]?.id
          });
          
          if (record.bulk_request_id && record.medicines?.[0]?.id) {
            // For bulk requests, use the first medicine's id
            const requestId = record.medicines[0].id;
            console.log('Using bulk request ID:', requestId);
            return requestId;
          }
          const requestId = record.id || 0;
          console.log('Using single request ID:', requestId);
          console.log('=== END GET REQUEST ID DEBUG ===');
          return requestId;
        };

        const actionConfig: Record<string, {
          title: string;
          icon: React.ReactNode;
          color: string;
          onClick: () => void;
        }> = {
          approve: {
            title: 'Approve Request',
            icon: <CheckCircleOutlined />,
            color: 'primary',
            onClick: () => {
              // Use bulk action for grouped requests, single action for individual requests
              if (record.medicines && record.medicines.length > 1) {
                // For bulk requests, don't pass approved_quantity - let each request use its own requested_quantity
                handleBulkWorkflowAction(record.request_number, 'approve', userRole);
              } else {
                // For individual requests, use the individual medicine's requested quantity
                const individualQuantity = record.requested_quantity || record.total_requested_quantity;
                handleWorkflowAction(getRequestId(), 'approve', userRole, {
                  approved_quantity: individualQuantity
                });
              }
            }
          },
          reject: {
            title: 'Reject Request',
            icon: <ExclamationCircleOutlined />,
            color: 'danger',
            onClick: () => {
              if (record.medicines && record.medicines.length > 1) {
                handleBulkWorkflowAction(record.request_number, 'reject', userRole);
              } else {
                handleWorkflowAction(getRequestId(), 'reject', userRole);
              }
            }
          },
          ship: {
            title: 'Mark as Shipped',
            icon: <SendOutlined />,
            color: 'primary',
            onClick: () => {
              if (record.medicines && record.medicines.length > 1) {
                handleBulkWorkflowAction(record.request_number, 'ship', userRole);
              } else {
                handleWorkflowAction(getRequestId(), 'ship', userRole);
              }
            }
          },
          deliver: {
            title: 'Mark as Delivered',
            icon: <DeliveredProcedureOutlined />,
            color: 'primary',
            onClick: () => {
              if (record.medicines && record.medicines.length > 1) {
                // For bulk requests, don't pass delivered_quantity - let each request use its own approved_quantity
                handleBulkWorkflowAction(record.request_number, 'deliver', userRole);
              } else {
                // For individual requests, use the individual medicine's approved quantity
                const individualQuantity = record.approved_quantity || record.requested_quantity || record.total_approved_quantity || record.total_requested_quantity;
                handleWorkflowAction(getRequestId(), 'deliver', userRole, {
                  delivered_quantity: individualQuantity
                });
              }
            }
          }
        };

        return (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedRequest(record);
                  setDetailsModalVisible(true);
                }}
              />
            </Tooltip>
            
            {availableActions.length > 0 && (
              <Dropdown
                menu={{
                  items: availableActions.map((action: string) => ({
                    key: action,
                    label: actionConfig[action]?.title,
                    icon: actionConfig[action]?.icon,
                    onClick: actionConfig[action]?.onClick,
                    danger: action === 'reject'
                  }))
                }}
                trigger={['click']}
              >
                <Button 
                  type="text" 
                  icon={<MoreOutlined />}
                  style={{
                    color: (userRole === 'branch' && (safeRequestType === 'seller_direct' || safeRequestType === 'seller_to_branch') && safeStatus === 'pending') ||
                           (userRole === 'seller' && (safeRequestType === 'branch_request' || safeRequestType === 'branch_to_seller') && safeStatus === 'pending')
                      ? '#1890ff' 
                      : undefined
                  }}
                />
              </Dropdown>
            )}

            {availableActions.length === 0 && record.status !== 'delivered' && record.status !== 'cancelled' && (
              <Tooltip title={`No actions available for ${userRole} role`}>
                <Button type="text" icon={<MoreOutlined />} disabled />
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];


  // Check if user is accessing the wrong route
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const isBranchUserOnMainRoute = (userData.branch || userData.branch_id) && !branchId;

  // Note: Removed automatic redirect to prevent endless loop
  // Branch users can stay on main route as long as filtering works correctly

  return (
    <div className="p-6">
      {isBranchUserOnMainRoute && (
        <Alert
          message="Branch User on Admin Route"
          description="You are viewing the admin inventory requests page. For a better experience, consider using the branch dashboard at /branch/inventory-requests."
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}
      <Tabs defaultActiveKey="requests">
        <TabPane tab="Inventory Requests" key="requests">
          {/* Statistics Cards */}
          {/* <Row gutter={16} className="mb-6">
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Requests"
                  value={pagination.total}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Pending Requests"
                  value={requests.filter(r => r.status === 'pending').length}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Delivered Requests"
                  value={requests.filter(r => r.status === 'delivered').length}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Low Stock Alerts"
                  value={lowStockAlerts.length}
                  prefix={<AlertOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row> */}

          {/* Filters and Actions */}
          <Card className="mb-4">
            <Row gutter={16} align="middle">
              {!branchId && (
                <Col span={6}>
                  <Select
                    placeholder="All Branches"
                    style={{ width: '100%' }}
                    allowClear
                    value={filters.branch_id || undefined}
                    onChange={(value) => setFilters({ ...filters, branch_id: value || '' })}
                  >
                    {branches.map(branch => (
                      <Option key={branch.branch_id} value={branch.branch_id}>
                        {branch.branch_name}
                      </Option>
                    ))}
                  </Select>
                </Col>
              )}
              <Col span={6}>
                <Select
                  placeholder="All Medicines"
                  style={{ width: '100%' }}
                  allowClear
                  value={filters.medicine_id || undefined}
                  onChange={(value) => setFilters({ ...filters, medicine_id: value || '' })}
                >
                  {medicines.map(medicine => (
                    <Option key={medicine.medicine_id} value={medicine.medicine_id}>
                      {medicine.medicine_name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="All Status"
                  style={{ width: '100%' }}
                  allowClear
                  value={filters.status || undefined}
                  onChange={(value) => setFilters({ ...filters, status: value || '' })}
                >
                  <Option value="pending">Pending</Option>
                  <Option value="approved">Approved</Option>
                  <Option value="shipped">Shipped</Option>
                  <Option value="delivered">Delivered</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Col>
              <Col span={4}>
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Start Date', 'End Date']}
                  onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                />
              </Col>
              <Col span={4}>
                <Input
                  placeholder="Search requests..."
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </Col>
            </Row>
            
            <Divider />
            
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  {/* Only show New Request button for seller/admin users (when branchId is not provided) */}
                  {!branchId && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setCreateModalVisible(true)}
                    >
                      New Request
                    </Button>
                  )}
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => fetchRequests(pagination?.current || 1, pagination?.pageSize || 10)}
                  >
                    Refresh
                  </Button>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button
                    type="default"
                    icon={<DownloadOutlined />}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Summary Statistics */}
          <Card className="mb-4">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Total Requests"
                  value={requests.length}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Medicines"
                  value={requests.reduce((sum, req) => sum + (req.total_medicines || 0), 0)}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Quantity"
                  value={requests.reduce((sum, req) => sum + (req.total_requested_quantity || 0), 0)}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Total Value"
                  value={requests.reduce((sum, req) => sum + (req.total_value || 0), 0)}
                  prefix="₹"
                  precision={2}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>
          </Card>

          {/* Requests Table */}
          <Card>
            <Table
              columns={columns}
              dataSource={Array.isArray(requests) ? requests : []}
              loading={loading}
              scroll={{ x: 1200 }}
              size="small"
              rowKey="request_id"
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} requests`,
                onChange: (page, pageSize) => {
                  setPagination({ ...pagination, current: page, pageSize: pageSize || 10 });
                  fetchRequests(page, pageSize || 10);
                }
              }}
            />
          </Card>
        </TabPane>

        <TabPane tab="Low Stock Alerts" key="alerts">
          <LowStockAlerts 
            showThresholdControl={true}
            threshold={40}
          />
        </TabPane>
      </Tabs>

      {/* Create Request Modal */}
      <Modal
        title="Create Inventory Request"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          setMedicineSearchTerm('');
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            medicines: [{ medicine_id: null, requested_quantity: 1, unit_price: 0, max_quantity: 0 }]
          }}
        >
          {/* Request Type Information */}
          <Alert
            message={`Request Type: ${branchId ? 'Branch Request' : 'Seller Direct'}`}
            description={branchId 
              ? 'This request will be sent to the seller on behalf of your branch.' 
              : 'This request will be processed as a direct seller request.'
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          {!branchId && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Branch</span>
                      <Button 
                        type="link" 
                        size="small" 
                        icon={<ReloadOutlined />}
                        onClick={fetchBranches}
                        loading={branchesLoading}
                        style={{ padding: 0 }}
                      >
                        Refresh
                      </Button>
                    </div>
                  }
                  name="branch_id"
                  rules={[{ required: true, message: 'Please select a branch' }]}
                >
                  <Select 
                    placeholder={branchesLoading ? "Loading branches..." : branches.length === 0 ? "No connected branches available" : "Choose branch for this request"}
                    disabled={branchesLoading || branches.length === 0}
                    loading={branchesLoading}
                    notFoundContent={branchesLoading ? "Loading branches..." : branches.length === 0 ? "No connected branches found. Please connect with branches first." : "No branches found"}
                    onChange={(branchId) => {
                      // Find the selected branch to get its relationship_id
                      const selectedBranch = branches.find(b => b.branch_id === branchId);
                      if (selectedBranch?.relationship_id) {
                        form.setFieldsValue({ relationship_id: selectedBranch.relationship_id });
                      }
                    }}
                  >
                    {branches.map(branch => (
                      <Option key={branch.branch_id} value={branch.branch_id}>
                        {branch.branch_name} {branch.branch_code && `(${branch.branch_code})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                {!branchesLoading && branches.length === 0 && (
                  <Alert
                    message="No Connected Branches"
                    description="You need to connect with branches before creating inventory requests. Go to the Branches page to send connection requests."
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </Col>
            </Row>
          )}

          <Form.Item
            label="Medicines"
            name="medicines"
            rules={[{ required: true, message: 'Please add at least one medicine' }]}
            extra="All medicines will be grouped under a single request with one receipt number"
          >
            <Form.List name="medicines">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card key={key} size="small" style={{ marginBottom: 12, border: '1px solid #d9d9d9' }}>
                      <Row gutter={16} align="middle">
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'medicine_id']}
                            rules={[{ required: true, message: 'Please select a medicine' }]}
                            label="Medicine"
                          >
                            <Select 
                              placeholder="Choose medicine to order" 
                              showSearch
                              filterOption={false}
                              onSearch={(value) => setMedicineSearchTerm(value)}
                              onChange={(value) => {
                                const selectedMedicine = medicines.find(m => m.medicine_id === value);
                                if (selectedMedicine) {
                                  // Update the specific medicine in the form
                                  const currentMedicines = form.getFieldValue('medicines') || [];
                                  const updatedMedicines = currentMedicines.map((med: any, index: number) => 
                                    index === name ? {
                                      ...med,
                                      medicine_id: value,
                                      unit_price: selectedMedicine.unit_price,
                                      max_quantity: selectedMedicine.quantity,
                                      seller_medicine_id: selectedMedicine.seller_medicine_id,
                                      seller_id: selectedMedicine.seller_id,
                                      seller_name: selectedMedicine.seller_name
                                    } : med
                                  );
                                  form.setFieldsValue({ medicines: updatedMedicines });
                                }
                              }}
                              loading={medicineSearchTerm !== debouncedMedicineSearch}
                              dropdownStyle={{
                                maxHeight: '300px',
                                overflow: 'auto'
                              }}
                              optionLabelProp="label"
                            >
                              {medicines.map(medicine => {
                                console.log('Rendering medicine option:', medicine);
                                return (
                                <Option key={medicine.medicine_id} value={medicine.medicine_id} label={medicine.medicine_name}>
                                  <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    width: '100%',
                                    padding: '8px 4px',
                                    minHeight: '50px',
                                    boxSizing: 'border-box'
                                  }}>
                                    <div style={{ 
                                      flex: 1, 
                                      paddingRight: '12px',
                                      minWidth: 0,
                                      overflow: 'hidden'
                                    }}>
                                      <div style={{ 
                                        fontWeight: 'bold', 
                                        fontSize: '14px',
                                        lineHeight: '1.3',
                                        marginBottom: '4px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}>
                                        {medicine.medicine_name}
                                      </div>
                                      <div style={{ 
                                        fontSize: '12px', 
                                        color: '#666',
                                        lineHeight: '1.2',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }}>
                                        Price: ₹{medicine.unit_price}
                                        {medicine.seller_name && (
                                          <span style={{ marginLeft: '8px', color: '#1890ff' }}>
                                            • {medicine.seller_name}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div style={{ 
                                      backgroundColor: medicine.quantity > 0 ? '#f6ffed' : '#fff2f0',
                                      color: medicine.quantity > 0 ? '#52c41a' : '#ff4d4f',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: 'bold',
                                      border: `1px solid ${medicine.quantity > 0 ? '#b7eb8f' : '#ffccc7'}`,
                                      minWidth: '50px',
                                      maxWidth: '60px',
                                      textAlign: 'center',
                                      flexShrink: 0,
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {medicine.quantity}
                                    </div>
                                  </div>
                                </Option>
                                );
                              })}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'requested_quantity']}
                            label="Quantity"
                            rules={[
                              { required: true, message: 'Enter quantity' },
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const medicines = getFieldValue('medicines');
                                  const currentMedicine = medicines[name];
                                  if (currentMedicine && currentMedicine.max_quantity && value > currentMedicine.max_quantity) {
                                    return Promise.reject(new Error(`Max available: ${currentMedicine.max_quantity}`));
                                  }
                                  return Promise.resolve();
                                },
                              }),
                            ]}
                          >
                            <InputNumber
                              min={1}
                              placeholder="Enter quantity"
                              style={{ width: '100%' }}
                              max={(() => {
                                const currentMedicine = form.getFieldValue('medicines')?.[name];
                                return currentMedicine?.max_quantity || undefined;
                              })()}
                              onChange={() => {
                                // Trigger form validation when quantity changes
                                form.validateFields([['medicines', name, 'requested_quantity']]);
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'unit_price']}
                            label="Unit Price"
                          >
                            <InputNumber
                              readOnly
                              style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                              prefix="₹"
                              precision={2}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <div style={{ paddingTop: '30px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                            <div>Available Stock:</div>
                            <div style={{ 
                              fontWeight: 'bold', 
                              color: (() => {
                                const currentMedicine = form.getFieldValue('medicines')?.[name];
                                if (!currentMedicine?.medicine_id) return '#999';
                                return currentMedicine.max_quantity > 0 ? '#1890ff' : '#ff4d4f';
                              })(),
                              fontSize: '14px'
                            }}>
                              {(() => {
                                const currentMedicine = form.getFieldValue('medicines')?.[name];
                                if (!currentMedicine?.medicine_id) return 'Select medicine';
                                return currentMedicine.max_quantity || 0;
                              })()}
                            </div>
                          </div>
                        </Col>
                        <Col span={3}>
                          <div style={{ paddingTop: '30px', textAlign: 'center' }}>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(name)}
                              disabled={fields.length === 1}
                              size="small"
                            >
                              Remove
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add({ medicine_id: null, requested_quantity: 1, unit_price: 0, max_quantity: 0 })}
                      block
                      icon={<PlusOutlined />}
                      style={{ height: '40px', borderStyle: 'dashed' }}
                    >
                      Add Another Medicine
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            label="Request Type"
            name="request_type"
            initialValue={branchId ? "branch_request" : "seller_direct"}
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Requested By"
            name="requested_by"
            initialValue={1}
            hidden
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            label="Relationship ID"
            name="relationship_id"
            hidden
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            label="Notes"
            name="notes"
          >
            <Input.TextArea rows={3} placeholder="Additional notes..." />
          </Form.Item>

          <Form.Item className="mb-0">
            <Space>
              <Button type="primary" htmlType="submit">
                Create Request
              </Button>
              <Button onClick={() => {
                setCreateModalVisible(false);
                setMedicineSearchTerm('');
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Request Details Modal */}
      <Modal
        title="Request Details"
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setSelectedRequest(null);
        }}
        width={800}
        footer={null}
      >
        {selectedRequest && (
          <div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Request Number">
                <strong>{selectedRequest.request_number}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Branch Information">
                <div>
                  <div><strong>{selectedRequest.branch_name}</strong> ({selectedRequest.branch_code})</div>
                  {selectedRequest.branch_location && (
                    <div className="text-muted">📍 {selectedRequest.branch_location}</div>
                  )}
                  {selectedRequest.branch_contact && (
                    <div className="text-muted">📞 {selectedRequest.branch_contact}</div>
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Seller Information">
                <div>
                  <div><strong>{selectedRequest.seller_name}</strong></div>
                  {selectedRequest.seller_id && (
                    <div className="text-muted">ID: {selectedRequest.seller_id}</div>
                  )}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Medicine Details">
                <div>
                  {(selectedRequest.medicines || [selectedRequest]).map((medicine, index) => (
                    <div key={medicine.medicine_id} style={{ marginBottom: index < (selectedRequest.medicines || [selectedRequest]).length - 1 ? '16px' : '0', paddingBottom: index < (selectedRequest.medicines || [selectedRequest]).length - 1 ? '16px' : '0', borderBottom: index < (selectedRequest.medicines || [selectedRequest]).length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                      <div><strong>{medicine.medicine_name}</strong></div>
                      {medicine.medicine_description && (
                        <div className="text-muted">{medicine.medicine_description}</div>
                      )}
                      {medicine.category_name && (
                        <div><Tag color="blue">{medicine.category_name}</Tag></div>
                      )}
                      {medicine.medicine_expiry && (
                        <div className="text-muted">📅 Expires: {new Date(medicine.medicine_expiry).toLocaleDateString()}</div>
                      )}
                    </div>
                  ))}
                </div>
              </Descriptions.Item>

              <Descriptions.Item label="Status & Timeline">
                <div>
                  <Tag color={
                    selectedRequest.status === 'pending' ? 'orange' :
                    selectedRequest.status === 'approved' ? 'blue' :
                    selectedRequest.status === 'shipped' ? 'purple' :
                    selectedRequest.status === 'delivered' ? 'green' : 'red'
                  }>
                    {selectedRequest.status_display || selectedRequest.status.toUpperCase()}
                  </Tag>
                  {selectedRequest.days_since_request !== undefined && (
                    <div className="text-muted">
                      {Math.round(selectedRequest.days_since_request)} days since request
                    </div>
                  )}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Request Type">
                <Tag color={getRequestTypeColor(selectedRequest.request_type)}>
                  {getRequestTypeDisplay(selectedRequest.request_type)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Requested Quantity">
                <div>
                  {(selectedRequest.medicines || [selectedRequest]).map((medicine, index) => (
                    <div key={medicine.medicine_id} style={{ marginBottom: index < (selectedRequest.medicines || [selectedRequest]).length - 1 ? '8px' : '0' }}>
                      <div><strong>{medicine.medicine_name}:</strong> {medicine.requested_quantity}</div>
                    </div>
                  ))}
                  <div style={{ marginTop: '8px', fontWeight: 'bold', color: '#1890ff' }}>
                    Total: {(selectedRequest.medicines || [selectedRequest]).reduce((sum, med) => sum + med.requested_quantity, 0)}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Approved Quantity">
                <div>
                  {(selectedRequest.medicines || [selectedRequest]).map((medicine, index) => (
                    <div key={medicine.medicine_id} style={{ marginBottom: index < (selectedRequest.medicines || [selectedRequest]).length - 1 ? '8px' : '0' }}>
                      <div><strong>{medicine.medicine_name}:</strong> {medicine.approved_quantity}</div>
                    </div>
                  ))}
                  <div style={{ marginTop: '8px', fontWeight: 'bold', color: '#1890ff' }}>
                    Total: {(selectedRequest.medicines || [selectedRequest]).reduce((sum, med) => sum + med.approved_quantity, 0)}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Delivered Quantity">
                <div>
                  {(selectedRequest.medicines || [selectedRequest]).map((medicine, index) => (
                    <div key={medicine.medicine_id} style={{ marginBottom: index < (selectedRequest.medicines || [selectedRequest]).length - 1 ? '8px' : '0' }}>
                      <div><strong>{medicine.medicine_name}:</strong> {medicine.delivered_quantity}</div>
                    </div>
                  ))}
                  <div style={{ marginTop: '8px', fontWeight: 'bold', color: '#1890ff' }}>
                    Total: {(selectedRequest.medicines || [selectedRequest]).reduce((sum, med) => sum + med.delivered_quantity, 0)}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Unit Prices">
                <div>
                  {(selectedRequest.medicines || [selectedRequest]).map((medicine, index) => (
                    <div key={medicine.medicine_id} style={{ marginBottom: index < (selectedRequest.medicines || [selectedRequest]).length - 1 ? '8px' : '0' }}>
                      <div><strong>{medicine.medicine_name}:</strong> ₹{medicine.unit_price ? Number(medicine.unit_price).toFixed(2) : 'N/A'}</div>
                    </div>
                  ))}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Request Date">
                {new Date(selectedRequest.request_date).toLocaleString()}
              </Descriptions.Item>
              {selectedRequest.approved_date && (
                <Descriptions.Item label="Approved Date">
                  {new Date(selectedRequest.approved_date).toLocaleString()}
                </Descriptions.Item>
              )}
              {selectedRequest.delivered_date && (
                <Descriptions.Item label="Delivered Date">
                  {new Date(selectedRequest.delivered_date).toLocaleString()}
                </Descriptions.Item>
              )}
              {selectedRequest.manager_name && (
                <Descriptions.Item label="Requested By">
                  {selectedRequest.manager_name}
                </Descriptions.Item>
              )}
              {selectedRequest.bulk_request_id && (
                <Descriptions.Item label="Bulk Request ID">
                  {selectedRequest.bulk_request_id}
                </Descriptions.Item>
              )}
              {selectedRequest.notes && (
                <Descriptions.Item label="Notes">
                  {selectedRequest.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider>Actions</Divider>
            
            <Space direction="vertical" style={{ width: '100%' }}>
              {(() => {
                const userRole = getUserRole();
                const availableActions = getAvailableActions(selectedRequest.status, selectedRequest.request_type, selectedRequest.medicines);

                if (availableActions.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                      <InfoCircleOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
                      <div>No actions available for your role</div>
                      <div style={{ fontSize: '12px' }}>
                        {userRole === 'branch' && (selectedRequest.request_type === 'branch_request' || selectedRequest.request_type === 'branch_to_seller') && selectedRequest.status === 'pending' 
                          ? 'Your request is pending approval from the seller'
                          : userRole === 'seller' && (selectedRequest.request_type === 'seller_direct' || selectedRequest.request_type === 'seller_to_branch') && selectedRequest.status === 'pending'
                          ? 'Your request is pending approval from the branch'
                          : 'This request cannot be modified at this time'
                        }
                      </div>
                    </div>
                  );
                }

                return availableActions.map(action => {
                  const medicines = selectedRequest.medicines || [selectedRequest];
                  
                  switch (action) {
                    case 'approve':
                      return (
                        <Button
                          key="approve"
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          block
                          onClick={() => {
                            handleStatusUpdate(medicines, 'approved', {
                              approved_quantity: medicines.reduce((sum, med) => sum + med.requested_quantity, 0)
                            });
                          }}
                        >
                          Approve Request
                        </Button>
                      );
                    
                    case 'reject':
                      return (
                        <Button
                          key="reject"
                          danger
                          icon={<ExclamationCircleOutlined />}
                          block
                          onClick={() => {
                            handleStatusUpdate(medicines, 'cancelled');
                          }}
                        >
                          Reject Request
                        </Button>
                      );
                    
                    case 'ship':
                      return (
                        <Button
                          key="ship"
                          type="primary"
                          icon={<TruckOutlined />}
                          block
                          onClick={() => {
                            handleStatusUpdate(medicines, 'shipped');
                          }}
                        >
                          Mark as Shipped
                        </Button>
                      );
                    
                    case 'deliver':
                      return (
                        <Button
                          key="deliver"
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          block
                          onClick={() => {
                            handleStatusUpdate(medicines, 'delivered', {
                              delivered_quantity: medicines.reduce((sum, med) => sum + med.approved_quantity, 0)
                            });
                          }}
                        >
                          Mark as Delivered
                        </Button>
                      );
                    
                    default:
                      return null;
                  }
                });
              })()}
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InventoryRequests;
